import { useEffect, useState } from "react";

/**
 * isMobile(), isTablet(), isWeb().
 * Original version of the layout util was using 1100 as the tablet max width.
 * Very inaccurate. Designed for tablet portrait; didn't work in tablet landscape mode.
 */

export enum LayoutEnv {
  Web = "web",
  Tablet = "tablet",
  Mobile = "mobile",
}

const MOBILE_MAX_WIDTH = 767;
const TABLET_MAX_WIDTH = 1100;

// Define the function first
export const getLayoutEnvFromWidth = (width: number): LayoutEnv => {
  if (width <= MOBILE_MAX_WIDTH) {
    return LayoutEnv.Mobile;
  } else if (width > MOBILE_MAX_WIDTH && width <= TABLET_MAX_WIDTH) {
    return LayoutEnv.Tablet;
  } else {
    return LayoutEnv.Web;
  }
};

// Global state for responsive layout
let currentLayoutEnv: LayoutEnv = LayoutEnv.Web;
const layoutListeners: Set<() => void> = new Set();

// Initialize layout on first load
if (typeof window !== "undefined") {
  currentLayoutEnv = getLayoutEnvFromWidth(window.innerWidth);
} else {
  // Default to Web layout for SSR
  currentLayoutEnv = LayoutEnv.Web;
}

export const getLayoutEnv = (): LayoutEnv => {
  return currentLayoutEnv;
};

// Subscribe to layout changes
export const subscribeToLayoutChanges = (
  callback: () => void
): (() => void) => {
  layoutListeners.add(callback);

  // Return unsubscribe function
  return () => {
    layoutListeners.delete(callback);
  };
};

// Update layout and notify listeners
const updateLayout = (width: number) => {
  const newLayoutEnv = getLayoutEnvFromWidth(width);
  if (newLayoutEnv !== currentLayoutEnv) {
    currentLayoutEnv = newLayoutEnv;
    layoutListeners.forEach((callback) => callback());
  }
};

// Set up global resize listener
if (typeof window !== "undefined") {
  let resizeTimeout: NodeJS.Timeout;

  window.addEventListener("resize", () => {
    // Debounce resize events
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      updateLayout(window.innerWidth);
    }, 100);
  });
}

export const isMobile = () => getLayoutEnv() === LayoutEnv.Mobile;
export const isTablet = () => getLayoutEnv() === LayoutEnv.Tablet;
export const isWeb = () => getLayoutEnv() === LayoutEnv.Web;

/**
 * isMobileV2(), isTabletV2(), isWebV2().
 * New version of the layout util using 1400 as the tablet max width and also checking userAgent.
 * Works even in tablet landscape mode.
 */

export enum LayoutEnvV2 {
  Web = "web",
  Tablet = "tablet",
  Mobile = "mobile",
}

export const MOBILE_MAX_WIDTH_V2 = 767;
export const TABLET_TYPICAL_WIDTH_V2 = 1100;
export const TABLET_MAX_WIDTH_V2 = 1400;

// Define the function first
export const getLayoutEnvFromWidthAndUserAgentV2 = (
  width: number,
  userAgent: string
): LayoutEnvV2 => {
  // Check if the user agent indicates a tablet device
  const isTabletUserAgent =
    /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i.test(userAgent);

  // Detect iOS Simulator including simulator of iPad (they report as macOS but have touch points)
  // Modern approach avoiding deprecated navigator.platform
  // Use > 2 touch points check to avoid false positives on Mac desktops with trackpads (which can report 1)
  const isMacWithTouch =
    typeof navigator !== "undefined" &&
    /Mac|Macintosh|MacIntel/i.test(userAgent) &&
    navigator.maxTouchPoints > 2;

  const isTabletDevice = isTabletUserAgent || isMacWithTouch;

  if (width <= MOBILE_MAX_WIDTH_V2) {
    return LayoutEnvV2.Mobile;
  } else if (width > MOBILE_MAX_WIDTH_V2 && width <= TABLET_TYPICAL_WIDTH_V2) {
    return LayoutEnvV2.Tablet;
  } else if (
    isTabletDevice &&
    width > TABLET_TYPICAL_WIDTH_V2 &&
    width <= TABLET_MAX_WIDTH_V2
  ) {
    return LayoutEnvV2.Tablet;
  } else {
    return LayoutEnvV2.Web;
  }
};

// Global state for responsive layout
let currentLayoutEnvV2: LayoutEnvV2 = LayoutEnvV2.Web;
const layoutListenersV2: Set<() => void> = new Set();

// Initialize layout on first load
if (typeof window !== "undefined") {
  currentLayoutEnvV2 = getLayoutEnvFromWidthAndUserAgentV2(
    window.innerWidth,
    typeof navigator !== "undefined" ? navigator.userAgent : ""
  );
} else {
  // Default to Web layout for SSR
  currentLayoutEnvV2 = LayoutEnvV2.Web;
}

export const getLayoutEnvV2 = (): LayoutEnvV2 => {
  return currentLayoutEnvV2;
};

// Subscribe to layout changes
export const subscribeToLayoutChangesV2 = (
  callback: () => void
): (() => void) => {
  layoutListenersV2.add(callback);

  // Return unsubscribe function
  return () => {
    layoutListenersV2.delete(callback);
  };
};

// Update layout and notify listeners
const updateLayoutV2 = (width: number) => {
  const newLayoutEnvV2 = getLayoutEnvFromWidthAndUserAgentV2(
    width,
    typeof navigator !== "undefined" ? navigator.userAgent : ""
  );
  if (newLayoutEnvV2 !== currentLayoutEnvV2) {
    currentLayoutEnvV2 = newLayoutEnvV2;
    layoutListenersV2.forEach((callback) => callback());
  }
};

// Set up global resize listener
if (typeof window !== "undefined") {
  let resizeTimeoutV2: NodeJS.Timeout;

  window.addEventListener("resize", () => {
    // Debounce resize events
    clearTimeout(resizeTimeoutV2);
    resizeTimeoutV2 = setTimeout(() => {
      updateLayoutV2(window.innerWidth);
    }, 100);
  });
}

export const isMobileV2 = () => getLayoutEnvV2() === LayoutEnvV2.Mobile;
export const isTabletV2 = () => getLayoutEnvV2() === LayoutEnvV2.Tablet;
export const isWebV2 = () => getLayoutEnvV2() === LayoutEnvV2.Web;

export const TABLET_PORTRAIT_MAX_WIDTH_V2 = 1000;

/**
 * Hook that tracks window.innerWidth and updates when the window is resized
 * @returns The current window inner width
 */
export const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return width;
};
